/**
 * Minimal colour maths for the theme contrast gate (CAP-7). Parses the
 * formats theme files use (hex, rgb[a], oklch) into sRGB and computes WCAG
 * 2.x contrast ratios.
 */

export interface Rgba {
  /** 0–1 gamma-encoded sRGB channels. */
  r: number;
  g: number;
  b: number;
  a: number;
}

const clamp01 = (n: number) => Math.min(1, Math.max(0, n));

function parseHex(input: string): Rgba | null {
  const m = /^#([0-9a-f]{3,8})$/i.exec(input.trim());
  if (!m) return null;
  let hex = m[1]!;
  if (hex.length === 3 || hex.length === 4) {
    hex = hex
      .split("")
      .map((c) => c + c)
      .join("");
  }
  if (hex.length !== 6 && hex.length !== 8) return null;
  const n = (i: number) => parseInt(hex.slice(i, i + 2), 16) / 255;
  return { r: n(0), g: n(2), b: n(4), a: hex.length === 8 ? n(6) : 1 };
}

function parseAlpha(part: string | undefined): number {
  if (!part) return 1;
  const p = part.trim();
  return clamp01(p.endsWith("%") ? parseFloat(p) / 100 : parseFloat(p));
}

function parseRgb(input: string): Rgba | null {
  const m = /^rgba?\(([^)]+)\)$/i.exec(input.trim());
  if (!m) return null;
  const [main, alpha] = m[1]!.split("/");
  const parts = main!.split(/[\s,]+/).filter(Boolean);
  if (parts.length < 3) return null;
  const ch = (p: string) => (p.endsWith("%") ? parseFloat(p) / 100 : parseFloat(p) / 255);
  return {
    r: clamp01(ch(parts[0]!)),
    g: clamp01(ch(parts[1]!)),
    b: clamp01(ch(parts[2]!)),
    a: alpha !== undefined ? parseAlpha(alpha) : parts[3] ? parseAlpha(parts[3]) : 1,
  };
}

function linearToGamma(c: number): number {
  const v = clamp01(c);
  return v <= 0.0031308 ? 12.92 * v : 1.055 * Math.pow(v, 1 / 2.4) - 0.055;
}

function parseOklch(input: string): Rgba | null {
  const m = /^oklch\(([^)]+)\)$/i.exec(input.trim());
  if (!m) return null;
  const [main, alpha] = m[1]!.split("/");
  const parts = main!.trim().split(/\s+/);
  if (parts.length < 3) return null;
  const L = parts[0]!.endsWith("%") ? parseFloat(parts[0]!) / 100 : parseFloat(parts[0]!);
  const C = parseFloat(parts[1]!);
  const H = (parseFloat(parts[2]!) * Math.PI) / 180;
  const a = C * Math.cos(H);
  const b = C * Math.sin(H);
  const l_ = L + 0.3963377774 * a + 0.2158037573 * b;
  const m_ = L - 0.1055613458 * a - 0.0638541728 * b;
  const s_ = L - 0.0894841775 * a - 1.291485548 * b;
  const l = l_ ** 3;
  const mm = m_ ** 3;
  const s = s_ ** 3;
  return {
    r: linearToGamma(4.0767416621 * l - 3.3077115913 * mm + 0.2309699292 * s),
    g: linearToGamma(-1.2684380046 * l + 2.6097574011 * mm - 0.3413193965 * s),
    b: linearToGamma(-0.0041960863 * l - 0.7034186147 * mm + 1.707614701 * s),
    a: parseAlpha(alpha),
  };
}

export function parseColor(input: string): Rgba {
  const c = parseHex(input) ?? parseRgb(input) ?? parseOklch(input);
  if (!c) throw new Error(`Unsupported colour value: ${input}`);
  return c;
}

/** `top` alpha-composited over an opaque `bottom`. */
export function composite(top: Rgba, bottom: Rgba): Rgba {
  const a = top.a;
  return {
    r: top.r * a + bottom.r * (1 - a),
    g: top.g * a + bottom.g * (1 - a),
    b: top.b * a + bottom.b * (1 - a),
    a: 1,
  };
}

/** `amount` (0–1) of `color` mixed into `base` (a "tint"). */
export function mix(color: Rgba, base: Rgba, amount: number): Rgba {
  return composite({ ...color, a: amount }, base);
}

function channelLuminance(c: number): number {
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

export function relativeLuminance(c: Rgba): number {
  return (
    0.2126 * channelLuminance(c.r) +
    0.7152 * channelLuminance(c.g) +
    0.0722 * channelLuminance(c.b)
  );
}

export function contrastRatio(a: Rgba, b: Rgba): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}
